import * as React from "react";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import * as Yup from "yup";
import {
  alpha,
  Backdrop,
  CircularProgress,
  FormControlLabel,
  Grid,
  Modal,
  styled,
  Switch,
} from "@mui/material";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout, userUpdate } from "../actions/userActions";
import InputComponent from "./forms/InputComponent";
import Form from "./forms/Form";
import SubmitButton from "./forms/SubmitButton";
import { toast } from "react-toastify";
import { USER_UPDATE_RESET } from "../constants/userConstants";
import { changeTheme } from "../actions/themeActions";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "20ch",
    },
  },
}));

const validationSchema = Yup.object().shape({
  name: Yup.string().required().label("Name").min(4),
  email: Yup.string().required().label("Email").email(),
});

const NavBar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };
  const [checked, setChecked] = React.useState(false);

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const handleLogout = () => {
    dispatch(logout());
    navigate("/", { replace: true });
  };

  const { palette } = useTheme();

  const updateUser = useSelector((state) => state.userUpdate);
  const { loading, success, error } = updateUser;

  React.useEffect(() => {
    if (success) {
      toast.success("Profile updated successfully");
      dispatch({
        type: USER_UPDATE_RESET,
      });
      setOpen(false);
    }
  }, [success, dispatch]);

  const handleSubmit = async ({ name, email, password }) => {
    dispatch(
      userUpdate({
        _id: userInfo._id,
        name,
        email,
        password,
      })
    );
  };

  const changeThemeFucn = () => {
    dispatch(changeTheme(checked ? "light" : "dark"));
  };

  return (
    <>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "none", md: "none" },
              backgroundColor: palette.primary.main,
            }}
          >
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>

            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: "none", md: "none" },
              }}
            ></Menu>
          </Box>
          {loading && (
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={true}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          )}

          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, display: { xs: "none", md: "none" } }}
          >
            LOGO
          </Typography>
          <Box
            sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}
            justifyContent={"right"}
          >
            <FormControlLabel
              control={
                <Switch
                  value={checked}
                  onChange={(e) => {
                    setChecked(e.target.checked);
                    changeThemeFucn();
                  }}
                />
              }
              label={checked ? "Light" : "Dark"}
            />
          </Box>

          <Box
            sx={{
              flexGrow: 0,
              display: {
                xs: "none",
                md: "flex",
              },
            }}
          >
            <Tooltip title="Open settings">
              <IconButton
                onClick={handleOpenUserMenu}
                sx={{ p: 0, ml: "auto" }}
              >
                <Avatar alt="Remy Sharp" src="/static/images/avatar/2.jpg" />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem onClick={handleCloseUserMenu}>
                <Typography textAlign="center">Dashboard</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOpen(true);
                  handleCloseUserMenu();
                }}
              >
                <Typography textAlign="center">Account</Typography>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Typography textAlign="center">Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </Container>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: palette.secondary.main,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Edit Profile
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Form
                onSubmit={handleSubmit}
                initialValues={{
                  name: userInfo.name,
                  email: userInfo.email,
                  password: "",
                }}
                validationSchema={validationSchema}
              >
                <InputComponent label="name" type="text" />
                <InputComponent label="email" type="email" />
                <InputComponent label="password" type="password" />
                <SubmitButton title={`Edit Profile`} />
              </Form>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};
export default NavBar;
